<template>
  <VueDatePicker
    v-model="internalTime"
    time-picker
    :dark="isDark"
    :disabled="disabled"
    :readonly="readonly"
    :placeholder="placeholder"
    :seconds-increment="showSeconds ? 1 : undefined"
    :enable-seconds="showSeconds"
    auto-apply
    text-input
    :teleport="false"
    class="b-form-timepicker-compat"
    :class="[size ? `dp-size-${size}` : '']"
  >
    <template #input-icon>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    </template>
  </VueDatePicker>
</template>

<script>
import { VueDatePicker } from '@vuepic/vue-datepicker'

export default {
  name: 'BFormTimepicker',
  components: { VueDatePicker },
  inheritAttrs: false,
  props: {
    modelValue: { type: String, default: undefined },
    value: { type: String, default: undefined },
    placeholder: { type: String, default: '' },
    size: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    locale: { type: String, default: undefined },
    showSeconds: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'input', 'change'],
  data() {
    return {
      isDark: document.body.classList.contains('dark-layout'),
      observer: null,
    }
  },
  computed: {
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value
    },
    internalTime: {
      get() {
        if (!this.currentValue) return null
        const parts = this.currentValue.split(':')
        return {
          hours: parseInt(parts[0], 10) || 0,
          minutes: parseInt(parts[1], 10) || 0,
          seconds: parseInt(parts[2], 10) || 0,
        }
      },
      set(v) {
        if (!v) {
          this.$emit('update:modelValue', '')
          this.$emit('input', '')
          this.$emit('change', '')
          return
        }
        const h = String(v.hours).padStart(2, '0')
        const m = String(v.minutes).padStart(2, '0')
        const s = String(v.seconds).padStart(2, '0')
        const str = this.showSeconds ? `${h}:${m}:${s}` : `${h}:${m}`
        this.$emit('update:modelValue', str)
        this.$emit('input', str)
        this.$emit('change', str)
      },
    },
  },
  mounted() {
    this.observer = new MutationObserver(() => {
      this.isDark = document.body.classList.contains('dark-layout')
    })
    this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect()
  },
}
</script>

<style>
.b-form-timepicker-compat {
  --dp-font-size: 1rem;
  --dp-border-radius: 0.357rem;
  --dp-input-padding: 0.438rem 2.5rem 0.438rem 2.2rem;
  --dp-input-icon-padding: 2.2rem;
  --dp-font-family: inherit;
  --dp-menu-min-width: 0;
  --dp-time-font-size: 0.95rem;
  --dp-time-inc-dec-button-size: 20px;
  --dp-button-height: 22px;
  --dp-menu-padding: 4px;
  --dp-common-padding: 4px;
}

.b-form-timepicker-compat .dp--input {
  min-height: 2.714rem;
}

.b-form-timepicker-compat .dp--clear-btn {
  inset-inline-end: 8px;
}

.b-form-timepicker-compat .dp--input-icon {
  left: 6px;
  display: flex;
  align-items: center;
  height: 100%;
}

.b-form-timepicker-compat .dp--menu {
  min-width: 0 !important;
  width: auto !important;
}

.b-form-timepicker-compat .dp--menu-inner {
  padding: 4px !important;
}

.b-form-timepicker-compat .dp--tp-wrap {
  padding: 0 !important;
}

.dp--overlay-relative:has(.dp--time-picker-overlay-container) {
  height: auto !important;
}

.dp--time-picker-overlay-container {
  height: auto !important;
}

.dp--time-picker-overlay-container .dp--time-col {
  gap: 0;
  padding: 0 6px !important;
}

.dp--time-picker-overlay-container .dp--time-display {
  padding: 2px 6px;
}

.dark-layout .b-form-timepicker-compat {
  --dp-background-color: #283046;
  --dp-text-color: #b4b7bd;
  --dp-border-color: #3b4253;
  --dp-menu-border-color: #3b4253;
  --dp-border-color-hover: #3b4253;
  --dp-border-color-focus: #7367f0;
  --dp-hover-color: #242b3d;
  --dp-hover-text-color: #d0d2d6;
  --dp-hover-icon-color: #d0d2d6;
  --dp-primary-color: #7367f0;
  --dp-primary-text-color: #fff;
  --dp-secondary-color: #676d7d;
  --dp-icon-color: #b4b7bd;
  --dp-disabled-color: #161d31;
  --dp-disabled-color-text: #676d7d;
  --dp-scroll-bar-background: #283046;
  --dp-scroll-bar-color: #3b4253;
  --dp-highlight-color: rgba(115, 103, 240, 0.15);
}

.dp-size-sm .dp--input {
  min-height: 2.142rem;
  font-size: 0.75rem;
}

.dp-size-lg .dp--input {
  min-height: 3rem;
  font-size: 1rem;
}
</style>
